import React, { useRef } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import {
  parseDotenv,
  ressembleAUnSecret,
  DotenvEntry,
} from '../utils/parseDotenv';

interface DotenvImportButtonProps {
  onImport: (entries: DotenvEntry[]) => void;
  /** `config` → ConfigMap (valeurs en clair) ; `secret` → Secret chiffre SOPS.
   *  Sert a orienter l'avertissement, pas a filtrer. */
  contexte: 'config' | 'secret';
  disabled?: boolean;
}

/** Au-dela, ce n'est pas un `.env` : on refuse avant de tout charger en memoire. */
const TAILLE_MAX_OCTETS = 512 * 1024;

/**
 * Charge un fichier `.env` depuis le poste et alimente le formulaire.
 *
 * Le fichier ne quitte PAS le navigateur : il est lu localement et les
 * variables partent ensuite par le chemin normal de l'API. Aucun upload -- ce
 * fichier contient des secrets, le transporter une fois de plus serait une
 * surface d'exposition sans contrepartie.
 */
const DotenvImportButton: React.FC<DotenvImportButtonProps> = ({
  onImport,
  contexte,
  disabled,
}) => {
  const { t } = (useTranslation as any)('deployments');
  const inputRef = useRef<HTMLInputElement>(null);

  const choisirFichier = () => inputRef.current?.click();

  const auChangement = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files?.[0];
    // Reinitialise tout de suite : sans ca, reimporter le MEME fichier apres
    // une correction ne declenche aucun evenement.
    e.target.value = '';
    if (!fichier) return;

    if (fichier.size > TAILLE_MAX_OCTETS) {
      toast.error(t('dotenvImport.tropVolumineux'));
      return;
    }

    let contenu: string;
    try {
      contenu = await fichier.text();
    } catch {
      toast.error(t('dotenvImport.lectureImpossible'));
      return;
    }

    const { entries, skipped, duplicates } = parseDotenv(contenu);

    if (entries.length === 0) {
      toast.error(t('dotenvImport.aucuneVariable'));
      return;
    }

    onImport(entries);
    toast.success(t('dotenvImport.succes', { count: entries.length }));

    // Les avertissements sont separes et volontairement explicites : une ligne
    // ecartee en silence se paie au demarrage du conteneur, bien plus tard.
    if (skipped.length > 0) {
      const apercu = skipped.slice(0, 3).map((s) => `L${s.line}`).join(', ');
      toast(t('dotenvImport.lignesIgnorees', { count: skipped.length, lignes: apercu }), {
        icon: '⚠️',
        duration: 8000,
      });
    }

    if (duplicates.length > 0) {
      toast(t('dotenvImport.doublons', { noms: duplicates.slice(0, 3).join(', ') }), {
        icon: '⚠️',
        duration: 8000,
      });
    }

    // Cote configuration, une variable au nom de secret partirait dans un
    // ConfigMap -- lisible par quiconque a `get configmap` sur le namespace.
    if (contexte === 'config') {
      const suspects = entries.map((v) => v.name).filter(ressembleAUnSecret);
      if (suspects.length > 0) {
        toast(t('dotenvImport.ressembleASecret', { noms: suspects.slice(0, 3).join(', ') }), {
          icon: '🔒',
          duration: 10000,
        });
      }
    }

    // Une valeur vide sur un secret veut dire « inchange » cote API : elle
    // reprend la valeur deja en place au lieu de la vider. Le dire ici, sinon
    // l'utilisateur croit avoir vide un secret qui ne l'est pas.
    if (contexte === 'secret') {
      const vides = entries.filter((v) => v.value === '').map((v) => v.name);
      if (vides.length > 0) {
        toast(t('dotenvImport.secretsVides', { noms: vides.slice(0, 3).join(', ') }), {
          icon: 'ℹ️',
          duration: 10000,
        });
      }
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        // PAS d'attribut `accept` : un fichier nomme exactement `.env` n'a pas
        // d'extension au sens du selecteur et aucun type MIME fiable -- le
        // filtre le grisait, c'est-a-dire exactement le cas principal. Le
        // filtrage n'apportait rien : le contenu est parse et valide ensuite,
        // et une taille excessive est refusee avant lecture.
        onChange={auChangement}
        className="hidden"
        data-testid="dotenv-file-input"
      />
      <button
        type="button"
        onClick={choisirFichier}
        disabled={disabled}
        title={t('dotenvImport.infobulle')}
        className="inline-flex items-center px-4 py-2 bg-surface-3 text-text-secondary border border-border-1 rounded-md hover:bg-surface-4 hover:text-text-primary transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowUpTrayIcon className="w-4 h-4 mr-2" aria-hidden="true" />
        {t('dotenvImport.bouton')}
      </button>
    </>
  );
};

export default DotenvImportButton;
